import { TimeFormat } from "src/gen/schema/models/auth";

export type FORMATS =
  // Full formats - 12H/24H pairs
  | "EEEEEE MMM dd, yyyy hh:mm:ss a" // Monday Oct 24, 2025 02:30:45 PM
  | "EEEEEE MMM dd, yyyy HH:mm:ss" // Monday Oct 24, 2025 14:30:45

  // Long formats - 12H/24H pairs
  | "MMM dd, yyyy hh:mm:ss a" // Oct 24, 2025, 02:30:45 PM
  | "MMM dd, yyyy HH:mm:ss" // Oct 24, 2025, 14:30:45

  // Short formats - 12H/24H pairs
  | "MM/dd/yyyy hh:mm:ss a" // 10/24/2025 02:30:45 PM
  | "MM/dd/yyyy HH:mm:ss" // 10/24/2025 14:30:45

  // Date with short time - 12H/24H pairs
  | "MM/dd/yyyy hh:mm a" // 10/24/2025 02:30 PM
  | "MM/dd/yyyy HH:mm" // 10/24/2025 14:30

  // Time only - 12H/24H pairs
  | "hh:mm:ss a" // 02:30:45 PM
  | "HH:mm:ss" // 14:30:45
  | "hh:mm a" // 02:30 PM
  | "HH:mm" // 14:30

  // Compact formats - 12H/24H pairs
  | "yyyyMMddhhmmss" // 20251024023045
  | "yyyyMMddHHmmss" // 20251024143045

  // Date only formats
  | "MM/dd/yyyy" // 10/24/2025
  | "MMM dd, yyyy" // Oct 24, 2025
  | "MM/dd" // 10/24
  | "EEE" // Mon
  | "MMM" // Oct
  | "yyyy" // 2025
  | "yyyy-MM-dd-h_mm_ss_a"
  | "yyyy-MM-dd-HH_mm_ss";

export enum FORMAT {
  // Full formats - 12H/24H pairs
  FULL = "EEEEEE MMM dd, yyyy hh:mm:ss a", // Monday Oct 24, 2025 02:30:45 PM
  FULL_24H = "EEEEEE MMM dd, yyyy HH:mm:ss", // Monday Oct 24, 2025 14:30:45

  // Long formats - 12H/24H pairs
  LONG = "MMM dd, yyyy hh:mm:ss a", // Oct 24, 2025, 02:30:45 PM
  LONG_24H = "MMM dd, yyyy HH:mm:ss", // Oct 24, 2025, 14:30:45

  // Short formats - 12H/24H pairs
  SHORT = "MM/dd/yyyy hh:mm:ss a", // 10/24/2025 02:30:45 PM
  SHORT_24H = "MM/dd/yyyy HH:mm:ss", // 10/24/2025 14:30:45

  // Date with short time - 12H/24H pairs
  DATE_TIME_SHORT = "MM/dd/yyyy hh:mm a", // 10/24/2025 02:30 PM
  DATE_TIME_SHORT_24H = "MM/dd/yyyy HH:mm", // 10/24/2025 14:30

  // Time only - 12H/24H pairs
  TIME_LONG = "hh:mm:ss a", // 02:30:45 PM
  TIME_LONG_24H = "HH:mm:ss", // 14:30:45
  TIME_SHORT = "hh:mm a", // 02:30 PM
  TIME_SHORT_24H = "HH:mm", // 14:30

  // Compact formats - 12H/24H pairs
  DATE_TIME = "yyyyMMddhhmmss", // 20251024023045
  DATE_TIME_24H = "yyyyMMddHHmmss", // 20251024143045

  // Date only formats
  MMDDYYYY = "MM/dd/yyyy", // 10/24/2025
  MMM_DD_YYYY = "MMM dd, yyyy", // Oct 24, 2025
  MMDD = "MM/dd", // 10/24
  EEE = "EEE", // Mon
  MMM = "MMM", // Oct
  YYYY = "yyyy", // 2025

  // File name formats (with no spaces)
  FILE_DATE_TIME = "yyyy-MM-dd-h_mm_ss_a",
  FILE_DATE_TIME_24H = "yyyy-MM-dd-HH_mm_ss"
}

const ALL_FORMAT: Partial<Record<FORMAT, Partial<Record<TimeFormat, FORMAT>>>> =
  {
    [FORMAT.FULL]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.FULL,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.FULL_24H
    },
    [FORMAT.LONG]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.LONG,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.LONG_24H
    },
    [FORMAT.SHORT]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.SHORT,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.SHORT_24H
    },
    [FORMAT.DATE_TIME]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.DATE_TIME,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.DATE_TIME_24H
    },
    [FORMAT.DATE_TIME_SHORT]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.DATE_TIME_SHORT,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.DATE_TIME_SHORT_24H
    },
    [FORMAT.TIME_SHORT]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.TIME_SHORT,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.TIME_SHORT_24H
    },
    [FORMAT.TIME_LONG]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.TIME_LONG,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.TIME_LONG_24H
    },
    [FORMAT.FILE_DATE_TIME]: {
      [TimeFormat.TIME_FORMAT_12HR]: FORMAT.FILE_DATE_TIME,
      [TimeFormat.TIME_FORMAT_24HR]: FORMAT.FILE_DATE_TIME_24H
    }
  };

export const getSpecificDateFormat = (
  dateFormat: FORMATS = FORMAT.LONG,
  hourFormat: TimeFormat = TimeFormat.TIME_FORMAT_24HR
): FORMATS => {
  return ALL_FORMAT[dateFormat]?.[hourFormat] ?? dateFormat;
};
